/**
 * inferSiteState — site-bound prior on the US state identity.
 *
 * DAT-FOLLOWUP-1 — Cross-state extraction hygiene.
 *
 * The DAT inspector (live-prod 2026-05-18) surfaced a Louisiana site
 * carrying `regulatory.state = "Tennessee"` because a Title-chain
 * document referenced a Tennessee Gas Pipeline easement. The extractor
 * had no notion of "site-bound" state — it treated any state token in
 * the document body as candidate evidence and stamped the SED's
 * regulatory section accordingly.
 *
 * This module exposes the deterministic site-state ground truth used
 * by:
 *
 *   1. `extractFromDocument` — to reject `regulatory.state` doc values
 *      that disagree with the site's known state UNLESS the document
 *      carries an explicit locus-binding phrase (`<site> in <state>`,
 *      `the site is located in <state>`, …).
 *   2. `detectEvidenceConflicts` — to fire a HIGH-severity conflict
 *      when an already-populated section has a state that disagrees
 *      with the site-state prior. Closes the gap where the doc was the
 *      FIRST write (provider 'unavailable') so the legacy two-axis
 *      conflict pass had nothing to compare against.
 *   3. `scripts/sedStateHygieneSweep.ts` — to walk every existing SED
 *      and flag mismatches in dry-run, with an opt-in write mode for
 *      admins to mark SEDs for rebuild.
 *
 * Resolution order, most-trusted first:
 *
 *   1. `electricity.data.state` — EIA-derived (full name or 2-letter).
 *   2. `regulatory.data.state` — 2-letter from `regulatoryDb`: a Census
 *      TIGER / FCC lookup, or — when both failed — a bounding-box
 *      estimate, which the section marks `stateResolution:
 *      'bbox-estimate'` (DAT-FOLLOWUP-4; unmarked on legacy SEDs).
 *   3. `estimateStateFromCoords(lat, lng)` — bounding-box fallback when
 *      neither provider populated. Synchronous, no network. Returns
 *      `null` outside the rough US bounding box or in coverage gaps.
 *
 * Returns the canonical 2-letter USPS code (uppercased), or `null` when
 * none of the probes succeed. The 2-letter form is the canonical key —
 * full state names are normalised through `STATE_NAME_TO_CODE`. Strings
 * that don't match either form return `null` (do NOT guess; downstream
 * consumers fall back to the existing extractor behaviour).
 * `inferSiteStateWithProvenance` also says which probe answered and
 * whether the answer is an estimate.
 */

import type { EvidenceSection, SiteEvidenceDocument } from '../types/siteEvidence';
import { estimateStateFromCoords } from '../services/dataProviders/regulatoryDb';

/**
 * Lowercase US state name → 2-letter code. Hand-curated (lowercase keys
 * so case doesn't matter at lookup). Covers the 50 + DC. Mirrors the
 * coverage of `US_STATE_CODES` in `conflictPrefilter.ts` so the two
 * registries stay in sync; the same Set under a different projection.
 */
const STATE_NAME_TO_CODE: ReadonlyMap<string, string> = new Map([
  ['alabama', 'AL'], ['alaska', 'AK'], ['arizona', 'AZ'], ['arkansas', 'AR'],
  ['california', 'CA'], ['colorado', 'CO'], ['connecticut', 'CT'], ['delaware', 'DE'],
  ['florida', 'FL'], ['georgia', 'GA'], ['hawaii', 'HI'], ['idaho', 'ID'],
  ['illinois', 'IL'], ['indiana', 'IN'], ['iowa', 'IA'], ['kansas', 'KS'],
  ['kentucky', 'KY'], ['louisiana', 'LA'], ['maine', 'ME'], ['maryland', 'MD'],
  ['massachusetts', 'MA'], ['michigan', 'MI'], ['minnesota', 'MN'], ['mississippi', 'MS'],
  ['missouri', 'MO'], ['montana', 'MT'], ['nebraska', 'NE'], ['nevada', 'NV'],
  ['new hampshire', 'NH'], ['new jersey', 'NJ'], ['new mexico', 'NM'], ['new york', 'NY'],
  ['north carolina', 'NC'], ['north dakota', 'ND'], ['ohio', 'OH'], ['oklahoma', 'OK'],
  ['oregon', 'OR'], ['pennsylvania', 'PA'], ['rhode island', 'RI'], ['south carolina', 'SC'],
  ['south dakota', 'SD'], ['tennessee', 'TN'], ['texas', 'TX'], ['utah', 'UT'],
  ['vermont', 'VT'], ['virginia', 'VA'], ['washington', 'WA'], ['west virginia', 'WV'],
  ['wisconsin', 'WI'], ['wyoming', 'WY'], ['district of columbia', 'DC'],
]);

/**
 * USPS two-letter codes (uppercase). Exported so callers can validate
 * a freshly-extracted token before treating it as a state at all.
 * Mirrors the set in `conflictPrefilter.ts`; the duplication is
 * intentional — this module imports cleanly without dragging in the
 * conflict prefilter's surface for tests that only need state
 * normalisation.
 */
export const US_STATE_CODES: ReadonlySet<string> = new Set(
  Array.from(STATE_NAME_TO_CODE.values()),
);

/**
 * Normalise a value (string OR something stringifiable) to a 2-letter
 * state code. Accepts:
 *   - 2-letter code (case-insensitive)        → uppercased code
 *   - Full state name (case-insensitive)      → mapped 2-letter code
 *   - Anything else (null, number, garbage)   → null
 *
 * Returns `null` on no match — do NOT guess. The DAT-FOLLOWUP
 * constitutional invariant: when in doubt, treat the value as
 * unrecognised state rather than silently coercing it to a guess.
 */
export function normaliseStateToCode(value: unknown): string | null {
  if (value == null) return null;
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;
  // 2-letter code path. Strict structural test — anything that doesn't
  // match exactly two ASCII letters falls through to the name lookup.
  if (/^[A-Za-z]{2}$/.test(trimmed)) {
    const upper = trimmed.toUpperCase();
    if (US_STATE_CODES.has(upper)) return upper;
    return null;
  }
  // Full-name path. Case-insensitive against the hand-curated map.
  const code = STATE_NAME_TO_CODE.get(trimmed.toLowerCase());
  return code ?? null;
}

/**
 * Where a SED section's `state` came from, as far as the section records it
 * (DAT-FOLLOWUP-4):
 *  - `'document'` — an uploaded document supplied the current value: a
 *    doc-filled slot, or a `docFieldSources.state` entry that still matches
 *    the value (the same currency rule `sedToText` applies);
 *  - `'derived'`  — the slot was filled by research, DAM, a prior report,
 *    authored context, intent or a CAR auto-fill chain, not by its provider;
 *  - `'estimate'` — regulatory-db's coordinate bounding-box estimate
 *    (`stateResolution: 'bbox-estimate'`);
 *  - `'provider'` — the section's own provider wrote it and recorded no
 *    estimate. For `electricity` that is the EIA provider, which resolves
 *    the state from Census TIGER, else FCC, and throws rather than guess.
 *    For `regulatory` it is a TIGER/FCC lookup on SEDs compiled since
 *    DAT-FOLLOWUP-4 but UNKNOWN on older ones (a legacy bounding-box
 *    estimate carries no marker), so it never proves a lookup there.
 *
 * Returns null when the section carries no state. Amendments fold into
 * section data without a per-field marker, so an amended state reads as
 * the origin the section records.
 */
export type SedStateOrigin = 'document' | 'derived' | 'estimate' | 'provider';

const DERIVED_STATUSES: ReadonlySet<string> = new Set([
  'verified-from-context', 'verified-from-report', 'verified-from-research',
  'verified-from-dam', 'verified-from-intent',
]);
const DERIVED_PROVENANCE: ReadonlySet<string> = new Set([
  'report-derived', 'report-overrides-provider', 'auto-fill',
]);

export function sedStateOrigin(
  section: Pick<EvidenceSection<unknown>, 'status' | 'data' | 'provenance' | 'docFieldSources'> | null | undefined,
): SedStateOrigin | null {
  const data = section?.data as { state?: unknown; stateResolution?: unknown } | null | undefined;
  if (!section || !data || data.state == null || data.state === '') return null;
  const docSource = section.docFieldSources?.state;
  if (
    section.status === 'verified-from-doc'
    || section.provenance === 'doc'
    || (docSource !== undefined && docSource.value === data.state)
  ) {
    return 'document';
  }
  if (DERIVED_STATUSES.has(section.status) || (section.provenance && DERIVED_PROVENANCE.has(section.provenance))) {
    return 'derived';
  }
  return data.stateResolution === 'bbox-estimate' ? 'estimate' : 'provider';
}

/** Which probe of `inferSiteStateWithProvenance` answered. */
export type SiteStateSource = 'electricity' | 'regulatory' | 'regulatory-estimate' | 'coords-estimate';

/**
 * Infer the canonical site state (2-letter code) from a SED, and say which
 * probe answered.
 *
 * Probes in order:
 *   1. `electricity.data.state`  — EIA-derived; full name or 2-letter.
 *   2. `regulatory.data.state`   — regulatoryDb's state. Reported as
 *      `'regulatory-estimate'` when the section marks it as a bounding-box
 *      estimate (DAT-FOLLOWUP-4); legacy SEDs never carry that marker, so
 *      `'regulatory'` is not proof of a lookup.
 *   3. `estimateStateFromCoords(location.lat, location.lng)` — bbox
 *      fallback (`'coords-estimate'`). Synchronous; no network.
 *
 * Returns null when nothing resolves.
 *
 * IMPORTANT: when the `regulatory` section's provenance is `'doc'` or
 * `'verified-from-doc'`, its `state` field is the very value we're
 * trying to second-guess (the DAT bug). The probe still trusts the
 * value when it's the only signal we have — the downstream guard
 * (`detectEvidenceConflicts` site-state pass) flags the disagreement
 * against `electricity` or coords separately.
 */
export function inferSiteStateWithProvenance(
  sed: Pick<SiteEvidenceDocument, 'electricity' | 'regulatory' | 'location'>,
): { code: string; source: SiteStateSource } | null {
  const electricityState = (sed.electricity?.data as { state?: unknown } | null)?.state;
  const fromElectricity = normaliseStateToCode(electricityState);
  if (fromElectricity) return { code: fromElectricity, source: 'electricity' };

  const regulatoryState = (sed.regulatory?.data as { state?: unknown } | null)?.state;
  const fromRegulatory = normaliseStateToCode(regulatoryState);
  if (fromRegulatory) {
    const estimated = sedStateOrigin(sed.regulatory as EvidenceSection<unknown> | undefined) === 'estimate';
    return { code: fromRegulatory, source: estimated ? 'regulatory-estimate' : 'regulatory' };
  }

  // Coordinate fallback. estimateStateFromCoords returns null outside
  // the rough US bounding box or in coverage gaps — match that semantic.
  const { lat, lng } = sed.location ?? { lat: NaN, lng: NaN };
  if (Number.isFinite(lat) && Number.isFinite(lng)) {
    const code = estimateStateFromCoords(lat, lng);
    if (code) return { code: code.toUpperCase(), source: 'coords-estimate' };
  }
  return null;
}

/**
 * Infer the canonical site state (2-letter code) from a SED: the code of
 * `inferSiteStateWithProvenance` (electricity → regulatory → coordinate
 * bounding box). Returns the 2-letter USPS code, or `null` when nothing
 * resolves.
 */
export function inferSiteStateFromSed(
  sed: Pick<SiteEvidenceDocument, 'electricity' | 'regulatory' | 'location'>,
): string | null {
  return inferSiteStateWithProvenance(sed)?.code ?? null;
}

/**
 * Detect a locus-binding phrase in the document text. A locus-binding
 * phrase is a sentence-level construct that explicitly attributes the
 * SITE (not a stray reference) to a state — `<site name> in
 * <state>`, `the site is located in <state>`, `<state> site`, …
 *
 * Pure / synchronous. Case-insensitive matching. Returns `true` when at
 * least one locus-binding phrase is found within the doc text for the
 * given candidate state (both 2-letter code and full name forms tried).
 *
 * Conservative on purpose: a false negative (missing a real
 * locus-binding phrase) costs the user one rerun + a manual override.
 * A false positive (rubber-stamping a cross-state assertion) re-opens
 * the very bug DAT-FOLLOWUP-1 exists to close. When uncertain, return
 * false.
 *
 * Accepted phrases (case-insensitive, matched against the haystack):
 *   - `(site|facility|plant|parcel|project|property) ... (in|located in|within|inside) <state>`
 *     within ~80 chars (so cross-paragraph hits don't trigger).
 *   - `<state>-based (site|facility|project|plant)`
 *   - `the (site|facility|plant|parcel|project|property) is (in|located in|within) <state>`
 *   - `<site-name> in <state>` when `siteName` is provided.
 */
export function hasLocusBindingPhrase(
  haystack: string,
  candidateState: string,
  options: { siteName?: string } = {},
): boolean {
  if (!haystack || !candidateState) return false;
  const code = normaliseStateToCode(candidateState);
  if (!code) return false;
  // Find the full name and the 2-letter code so the haystack can mention
  // either. Full name first — fewer false positives than the bare code.
  const fullName = Array.from(STATE_NAME_TO_CODE.entries())
    .find(([, v]) => v === code)?.[0];
  const tokens: string[] = [];
  if (fullName) tokens.push(fullName);
  tokens.push(code);
  // Escape for regex. State tokens never contain regex meta in our set,
  // but defensively-cheap.
  const escape = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const stateAlt = tokens.map(escape).join('|');
  // Locus subject vocabulary. Keep narrow so a sentence like "Tennessee
  // Gas Pipeline" doesn't false-positive — that's exactly the bug we're
  // closing.
  const subject = `(site|facility|plant|parcel|project|property)`;
  const verb = `(is\\s+)?(in|located\\s+in|within|inside|sited\\s+in)`;
  // The 80-char proximity window lets "the site, with all its boundary
  // adjustments, is located in Louisiana" pass while rejecting
  // cross-paragraph hits.
  const patterns: RegExp[] = [
    // "...site is located in <STATE>" / "...site in <STATE>"
    new RegExp(`\\b${subject}\\b[^.\\n]{0,80}?\\b${verb}\\b\\s+(${stateAlt})\\b`, 'i'),
    // "<STATE>-based (site|facility|...)" / "<STATE>-based project"
    new RegExp(`\\b(${stateAlt})\\s*[-–]\\s*based\\s+${subject}\\b`, 'i'),
  ];
  if (options.siteName && options.siteName.trim().length > 0) {
    const siteNameEsc = escape(options.siteName.trim());
    // "<site name> in <STATE>" / "<site name> ... located in <STATE>"
    patterns.push(
      new RegExp(`\\b${siteNameEsc}\\b[^.\\n]{0,80}?\\b${verb}\\b\\s+(${stateAlt})\\b`, 'i'),
    );
  }
  for (const re of patterns) {
    if (re.test(haystack)) return true;
  }
  return false;
}
